import React from "react";
import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/**
 * LineGrid
 * MUI Grid with MultiCarousel-style gap handling + border rendering
 *
 * Props:
 * @param {React.ReactNode[]} children - MUI Grid item들 [Required]
 * @param {number} gap - 아이템 간격(px) [Optional, 기본값: 16]
 * @param {string} borderColor - 선 색상 [Optional, 기본값: 'text.secondary']
 * @param {number} gridColumns - 그리드 컬럼 수 [Optional, 기본값: 4]
 * @param {string} breakpoint - 모바일/PC 전환 브레이크포인트 [Optional, 기본값: 'md']
 */
const LineGrid = ({
	children,
	gap = 16,
	borderColor = "text.secondary",
	gridColumns = 4,
	breakpoint = "md",
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(breakpoint));

	// children을 배열로 고정
	const childrenArray = React.Children.toArray(children);

	// 각 아이템의 실제 span(12그리드 기준)을 계산
	const spans = childrenArray.map((child) => {
		if (!React.isValidElement(child)) return 12;
		const gridSize = child.props.size || {};
		let actualSize;
		if (isMobile) {
			actualSize = gridSize.xs || gridSize.sm || 12;
		} else {
			// PC: md > lg > xl 우선 적용
			actualSize = gridSize.md || gridSize.lg || gridSize.xl || 12;
		}
		const span = Math.max(
			1,
			Math.min(12, typeof actualSize === "number" ? actualSize : 12)
		);
		return span;
	});

	// 누적 폭으로 행(row), 시작 열(colStart), span 계산
	const layout = [];
	let rowIndex = 0;
	let colCursor = 0; // 0..12

	spans.forEach((span, i) => {
		if (span > 12 - colCursor) {
			rowIndex += 1;
			colCursor = 0;
		}

		layout[i] = { row: rowIndex, colStart: colCursor, span };
		colCursor += span;

		if (colCursor >= 12) {
			rowIndex += 1;
			colCursor = 0;
		}
	});

	return (
		<Grid container spacing={gap / 8} disableEqualOverflow>
			{childrenArray.map((child, index) => {
				if (!React.isValidElement(child)) return child;

				const meta = layout[index] || { row: 0, colStart: 0, span: 12 };
				const isFirstRow = meta.row === 0;
				const isFirstInRow = meta.colStart === 0;
				const isLastItem = index === childrenArray.length - 1;
				const isEndOfRow = meta.colStart + meta.span >= 12;

				return React.cloneElement(child, {
					...child.props,
					sx: {
						...child.props.sx,
						position: "relative",
						// 세로 보더: 행의 첫 아이템이 아니면 좌측에 보더
						"&::before": !isFirstInRow
							? {
									content: '""',
									position: "absolute",
									left: `-${gap / 2}px`,
									top: 0,
									bottom: 0,
									width: "1px",
									backgroundColor: borderColor,
							  }
							: {},
						// 가로 보더: 첫 행이 아니면 상단에 보더
						"&::after": !isFirstRow
							? {
									content: '""',
									position: "absolute",
									top: `-${gap / 2}px`,
									left: 0,
									right: 0,
									height: "1px",
									backgroundColor: borderColor,
							  }
							: {},
						// 마지막 줄이 꽉 차지 않아도 마지막 아이템 우측 보더 보정
						...(isLastItem && !isEndOfRow
							? {
									boxShadow: `${gap / 2}px 0 0 0 ${borderColor}`,
							  }
							: {}),
					},
				});
			})}
		</Grid>
	);
};

export default LineGrid;
